import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogContent,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import { useReactToPrint } from 'react-to-print';
import axios from 'axios';
import { AnimatePresence } from 'framer-motion';
import { IoMdCloseCircle } from 'react-icons/all';
import { useMainProvider } from '../../context/MainProvider';
import { CRUDModes } from '../../interfaces/CRUD';
import { AlertTypes } from '../../interfaces/MainContextInitialValues';
import { PrimaryDescription } from '../../components/PrimaryDescription/PrimaryDescription';
import { CMTextfield } from '../../components/Forms/CMTextfield';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import CenteredForm from '../../index.styled';
import { PrimarySubtitle } from '../../components/PrimarySubtitle/PrimarySubtitle';
import { setHeaders } from '../../utils/headerSetter';
import { CMTextArea } from '../../components/Forms/CMTextArea';
import { CMInputLabel } from '../../components/Forms/InputLabel';
import { Medicamento } from '../../interfaces/Receta';
import { NoPaddingButton } from '../../components/NoPaddingButton/NoPaddingButton';
import { StandardText } from '../../components/StandardText/StandardText';
import {
  NotaIndicaciones,
  NotaIndicacionesMedicamento,
  NotaIndicacionesMedicamentoForm,
  NotaIndicacionesPost,
} from '../../interfaces/NotaIndicaciones';
import { Patient } from '../../interfaces/Patients';
import { Doctor } from '../../interfaces/Doctors';
import NotaIndicacionesDocument from './NotaIndicacionesDocument';
import { postWithToken } from '../../utils/server';
import DialogCustomTitle from '../../components/DialogCustomTitle/DialogCustomTitle';
import PatientData from '../../components/PatientData/PatientData';
import { canPrintNote } from '../../utils/fns';
import { isIOSApp } from '../../interfaces/WebkitWindow';
import MobileDownloadMessage from '../../components/MobileDownloadMessage';
import EmptyClosure from '../../utils/closures';

interface FormProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  mode: CRUDModes;
  onSuccess: () => void;
  values?: NotaIndicaciones;
  closeFunction?: () => void;
}

const initialValues: NotaIndicaciones = {
  paciente_id: 0,
  doctor_id: 0,
  soluciones: '',
  terapia_respiratoria: '',
  estudios_indicados: '',
  medidas_generales: '',
  dieta: '',
  medicamentos_fuera_cuadro: '',
};

const NotaIndicacionesForm = ({
  open,
  setOpen,
  mode,
  onSuccess,
  values,
  closeFunction,
}: FormProps): JSX.Element => {
  const {
    loading,
    setLoading,
    setAlertMessage,
    setAlertType,
    setOpenAlert,
    user,
    currentPatient,
    handleLogout,
  } = useMainProvider();
  const mobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [formValues, setFormValues] = useState(initialValues);
  const [medicamentos, setMedicamentos] = useState<Medicamento[]>([]);
  const [showExtraMedicineField, setShowExtraMedicineField] = useState(false);
  const [selectedMedicamentos, setSelectedMedicamentos] = useState<
    NotaIndicacionesMedicamentoForm[]
  >([]);
  const [medicineSearch, setMedicineSearch] = useState('');
  const [doctorInformation, setDoctorInformation] = useState<Doctor | null>(null);
  const [patientInformation, setPatientInformation] = useState<Patient | null>(null);
  const printRef = useRef<HTMLInputElement>(null);
  const fieldsDisabled = useMemo(
    () => mode === CRUDModes.Update && formValues.doctor_id !== user?.id,
    [mode, formValues, user?.id],
  );

  const getDoctorInformation = useCallback(async () => {
    try {
      const doctorResult = await postWithToken<
        {
          doctorId?: number;
        },
        Doctor
      >(
        '/doctor/get-doctor',
        {
          doctorId: user?.id,
        },
        handleLogout,
      );
      if (doctorResult.success) {
        setDoctorInformation(doctorResult.data);
      }
    } catch (e) {
      handleLogout(e);
      console.log(e);
    }
  }, [handleLogout, user?.id]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: 'background: white !important;',
  });

  useEffect(() => {
    setFormValues(initialValues);
    if (!values) {
      if (currentPatient) {
        setPatientInformation(currentPatient.initialData as Patient);
        getDoctorInformation().then(EmptyClosure).catch(EmptyClosure);
      }
    } else {
      setPatientInformation(values.patientInformation as Patient);
      setDoctorInformation(values.doctorInformation as Doctor);
      setFormValues(values);
      if (values.medicamentos_fuera_cuadro) {
        setShowExtraMedicineField(true);
      }
      setSelectedMedicamentos(values.medicamentos_seleccionados || []);
    }
  }, [currentPatient, getDoctorInformation, open, values]);

  const formik = useFormik<NotaIndicaciones>({
    initialValues: formValues,
    enableReinitialize: true,
    onSubmit: async (form) => {
      try {
        setLoading(true);
        const entry: NotaIndicacionesPost = {
          nota: form,
          medicamentos: selectedMedicamentos
            .filter((med) => med.medicamento !== undefined)
            .map((med): NotaIndicacionesMedicamento => {
              return {
                id_medicamento: med.medicamento?.id || 0,
                dosis: med.dosis,
                via: med.via,
                marca_recomendada: med.marca_recomendada,
                periodicidad: med.periodicidad,
                numero_dias: med.numero_dias,
                numero_cajas: med.numero_cajas,
              };
            }),
        };
        const creationResult = await axios.post<{
          newEntry: NotaIndicacionesPost;
        }>(
          `${process.env.REACT_APP_SERVER_URL}/notaIndicaciones/${
            mode === CRUDModes.Create ? 'post' : 'update'
          }`,
          {
            entry,
            patientId:
              currentPatient && mode === CRUDModes.Create ? currentPatient.id : undefined,
          },
          setHeaders(true, user ? user.token : undefined),
        );
        if (creationResult.status === 200) {
          setAlertType(AlertTypes.Success);
          setAlertMessage(
            `¡Nota de indicaciones ${
              mode === CRUDModes.Create ? 'creada' : 'actualizada'
            } exitosamente!`,
          );
          formik.resetForm();
          setFormValues(initialValues);
          setOpenAlert(true);
          setLoading(false);
          setMedicineSearch('');
          setSelectedMedicamentos([]);
          setShowExtraMedicineField(false);
          setOpen(false);
          onSuccess();
          if (closeFunction) closeFunction();
        } else {
          setLoading(false);
        }
      } catch (e: any) {
        handleLogout(e);
        if (JSON.stringify(e.response.data)) {
          setLoading(false);
          setAlertType(AlertTypes.Error);
          setAlertMessage(
            `Error al guardar notas de indicaciones: ${JSON.stringify(e.response.data)} `,
          );
          setOpenAlert(true);
        } else {
          setLoading(false);
          setAlertType(AlertTypes.Error);
          setAlertMessage(`Error en el servidor: ${e}`);
          setOpenAlert(true);
        }
      }
    },
  });

  const searchOptionsMedicines = async (searchterm: string) => {
    try {
      const results = await axios.post<Medicamento[]>(
        `${process.env.REACT_APP_SERVER_URL}/receta/search-medicamentos`,
        {
          searchterm,
        },
        setHeaders(true, user ? user.token : undefined),
      );
      setMedicamentos(results.data);
    } catch (e) {
      handleLogout(e);
      setAlertType(AlertTypes.Error);
      setAlertMessage('Error al obtener medicamentos');
      setOpenAlert(true);
    }
  };

  const addMedicamento = () => {
    setMedicineSearch('');
    if (!selectedMedicamentos.find((medicamento) => !medicamento.medicamento))
      setSelectedMedicamentos([
        ...selectedMedicamentos,
        {
          marca_recomendada: '',
          dosis: '',
          via: '',
          periodicidad: '',
          numero_cajas: '',
          numero_dias: 0,
        },
      ]);
  };

  const updateSelectionMedicina = (
    index: number,
    updatedSelection?: Medicamento | null,
  ) => {
    const newArray = [...selectedMedicamentos];
    newArray[index] = {
      ...newArray[index],
      medicamento: updatedSelection || undefined,
      via: updatedSelection ? updatedSelection.via || '' : '',
      marca_recomendada: updatedSelection?.DESCRIPCION
        ? updatedSelection?.DESCRIPCION
        : '',
    };
    setSelectedMedicamentos(newArray);
  };

  const deleteMedicamento = (index: number) => {
    const newEntries = [...selectedMedicamentos];
    newEntries.splice(index, 1);
    setSelectedMedicamentos(newEntries);
  };

  const handleReset = () => {
    formik.resetForm();
    setFormValues(initialValues);
    setMedicineSearch('');
    setSelectedMedicamentos([]);
    setShowExtraMedicineField(false);
    setOpen(false);
    if (closeFunction) closeFunction();
  };

  return (
    <Dialog
      maxWidth='xl'
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handleReset();
        }
      }}
    >
      <DialogCustomTitle onClose={handleReset} mode={mode} title='nota de indicaciones' />

      <DialogContent>
        <AnimatePresence exitBeforeEnter>
          {' '}
          <CenteredForm onSubmit={formik.handleSubmit}>
            <Grid
              container
              direction='row'
              justifyContent='center'
              alignItems='center'
              spacing={2}
            >
              <Grid item xs={12}>
                <PatientData patientInformation={patientInformation || undefined} />
              </Grid>
              <Grid item xs={12}>
                <PrimaryDescription mobile style={{ margin: '0' }}>
                  Recuerda llenar la información correctamente
                </PrimaryDescription>
              </Grid>
              <Grid item xs={12}>
                <CMInputLabel mobile>Dieta</CMInputLabel>
                <CMTextArea
                  $spacing={2}
                  disabled={fieldsDisabled}
                  id='dieta'
                  name='dieta'
                  value={formik.values.dieta}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  minRows={2}
                  placeholder='Dieta'
                />
              </Grid>
              <Grid item xs={12}>
                <CMInputLabel mobile>Soluciones</CMInputLabel>
                <CMTextArea
                  $spacing={2}
                  disabled={fieldsDisabled}
                  id='soluciones'
                  name='soluciones'
                  value={formik.values.soluciones}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  minRows={2}
                  placeholder='Soluciones'
                />
              </Grid>
              <Grid item xs={12}>
                <PrimarySubtitle mobile>Medicamentos indicados</PrimarySubtitle>
                {!fieldsDisabled ? (
                  <PrimaryDescription mobile style={{ margin: '0' }}>
                    {mobile ? 'Toca el' : 'Haz click en el'} botón de &quot;Añadir
                    medicamento&quot; para capturar el tratamiento de un nuevo
                    medicamento.
                  </PrimaryDescription>
                ) : null}
              </Grid>
              {selectedMedicamentos.length
                ? selectedMedicamentos.map((medicamento, index) => {
                    const key = `divMedicamento#${
                      medicamento.medicamento?.id || '-'
                    }${index}`;
                    const autocompleteKey = `autocompleteMedicamento#${
                      medicamento.medicamento?.id || '-'
                    }${index}`;
                    const inputKey = `inputMedicamento#${
                      medicamento.medicamento?.id || '-'
                    }${index}`;
                    return (
                      <Grid key={key} item xs={12}>
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'nowrap',
                          }}
                        >
                          <div
                            style={{
                              borderRadius: '12px',
                              backgroundColor: '#f4f4f4',
                              padding: '0.5rem',
                              width: 'calc(100% - 2rem)',
                            }}
                          >
                            <Grid
                              container
                              direction='row'
                              justifyContent='flex-start'
                              alignItems='center'
                              spacing={1}
                            >
                              <Grid item xs={12} md={6}>
                                <Autocomplete
                                  disabled={fieldsDisabled}
                                  id={autocompleteKey}
                                  filterOptions={(x) => x}
                                  options={medicamentos}
                                  filterSelectedOptions
                                  noOptionsText='Escribe para buscar un medicamento'
                                  getOptionLabel={(option) => {
                                    if (option) {
                                      return `${option.sales}`;
                                    }
                                    return '';
                                  }}
                                  renderOption={(props, option?: Medicamento) => {
                                    if (option) {
                                      return (
                                        <li {...props} key={option.id}>
                                          <StandardText>
                                            <b>{option.DESCRIPCION}</b>
                                          </StandardText>
                                          {option.via ? (
                                            <StandardText>{option.via}</StandardText>
                                          ) : null}
                                        </li>
                                      );
                                    }
                                    return null;
                                  }}
                                  onInputChange={(_event, value) =>
                                    searchOptionsMedicines(value)
                                  }
                                  value={medicamento.medicamento}
                                  onChange={(_e, value) => {
                                    updateSelectionMedicina(index, value);
                                  }}
                                  renderInput={(params) => (
                                    <CMTextfield
                                      $nomargin
                                      {...params}
                                      label='Medicamento genérico'
                                      placeholder='Medicamento genérico'
                                      name={inputKey}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <CMTextfield
                                  $nomargin
                                  disabled={fieldsDisabled}
                                  type='text'
                                  label='Nombre patente recomendado'
                                  placeholder='Nombre patente recomendado'
                                  value={medicamento.marca_recomendada}
                                  onChange={(e) => {
                                    const newArray = [...selectedMedicamentos];
                                    newArray[index] = {
                                      ...newArray[index],
                                      marca_recomendada: e.target.value,
                                    };
                                    setSelectedMedicamentos(newArray);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <CMTextfield
                                  disabled={fieldsDisabled}
                                  $nomargin
                                  type='text'
                                  label='Vía'
                                  placeholder='Vía'
                                  value={medicamento.via}
                                  onChange={(e) => {
                                    const newArray = [...selectedMedicamentos];
                                    newArray[index] = {
                                      ...newArray[index],
                                      via: e.target.value,
                                    };
                                    setSelectedMedicamentos(newArray);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <CMTextfield
                                  $nomargin
                                  type='text'
                                  label='Dosis'
                                  disabled={fieldsDisabled}
                                  placeholder='Dosis'
                                  value={medicamento.dosis}
                                  onChange={(e) => {
                                    const newArray = [...selectedMedicamentos];
                                    newArray[index] = {
                                      ...newArray[index],
                                      dosis: e.target.value,
                                    };
                                    setSelectedMedicamentos(newArray);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <CMTextfield
                                  $nomargin
                                  type='text'
                                  disabled={fieldsDisabled}
                                  label='Periodicidad'
                                  placeholder='Periodicidad'
                                  value={medicamento.periodicidad}
                                  onChange={(e) => {
                                    const newArray = [...selectedMedicamentos];
                                    newArray[index] = {
                                      ...newArray[index],
                                      periodicidad: e.target.value,
                                    };
                                    setSelectedMedicamentos(newArray);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <CMTextfield
                                  $nomargin
                                  type='number'
                                  inputProps={{
                                    inputMode: 'numeric',
                                  }}
                                  disabled={fieldsDisabled}
                                  label='Dias de tratamiento'
                                  placeholder='Dias de tratamiento'
                                  value={medicamento.numero_dias}
                                  onChange={(e) => {
                                    const newArray = [...selectedMedicamentos];
                                    newArray[index] = {
                                      ...newArray[index],
                                      numero_dias: e.target.value
                                        ? Number(e.target.value)
                                        : 0,
                                    };
                                    setSelectedMedicamentos(newArray);
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <CMTextfield
                                  $nomargin
                                  type='string'
                                  disabled={fieldsDisabled}
                                  label='Presentación / No. Cajas'
                                  placeholder='Presentación / No. Cajas'
                                  value={medicamento.numero_cajas}
                                  onChange={(e) => {
                                    const newArray = [...selectedMedicamentos];
                                    newArray[index] = {
                                      ...newArray[index],
                                      numero_cajas: e.target.value,
                                    };
                                    setSelectedMedicamentos(newArray);
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </div>
                          <NoPaddingButton
                            type='button'
                            onClick={() => deleteMedicamento(index)}
                            disabled={fieldsDisabled}
                          >
                            <IoMdCloseCircle
                              style={{
                                fontSize: '1.5rem',
                                marginLeft: '0.5rem',
                                cursor: 'pointer',
                              }}
                            />
                          </NoPaddingButton>
                        </div>
                      </Grid>
                    );
                  })
                : null}
              {!fieldsDisabled ? (
                <Grid item xs={12}>
                  <NoPaddingButton
                    disabled={fieldsDisabled}
                    type='button'
                    onClick={addMedicamento}
                    style={{ padding: '0.5rem', fontSize: '1rem' }}
                  >
                    + Añadir medicamento
                  </NoPaddingButton>
                </Grid>
              ) : null}
              {showExtraMedicineField ? (
                <Grid item xs={12}>
                  <CMInputLabel mobile>Medicamentos fuera del cuadro médico</CMInputLabel>
                  <CMTextArea
                    disabled={fieldsDisabled}
                    $spacing={2}
                    id='medicamentos_fuera_cuadro'
                    name='medicamentos_fuera_cuadro'
                    value={formik.values.medicamentos_fuera_cuadro}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    minRows={2}
                    placeholder='Información de los medicamentos'
                  />
                </Grid>
              ) : (
                <>
                  {!fieldsDisabled ? (
                    <Grid item xs={12}>
                      <NoPaddingButton
                        type='button'
                        onClick={() => setShowExtraMedicineField(true)}
                        style={{
                          padding: '0.5rem',
                          fontSize: '1rem',
                          textAlign: 'start',
                        }}
                      >
                        + Añadir medicamentos fuera del cuadro médico
                      </NoPaddingButton>
                    </Grid>
                  ) : null}
                </>
              )}
              <Grid item xs={12}>
                <CMInputLabel mobile>Terapia respiratoria</CMInputLabel>
                <CMTextArea
                  $spacing={2}
                  disabled={fieldsDisabled}
                  id='terapia_respiratoria'
                  name='terapia_respiratoria'
                  value={formik.values.terapia_respiratoria}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  minRows={2}
                  placeholder='Descripción'
                />
              </Grid>
              <Grid item xs={12}>
                <CMInputLabel mobile>Estudios Indicados</CMInputLabel>
                <CMTextArea
                  $spacing={2}
                  disabled={fieldsDisabled}
                  id='estudios_indicados'
                  name='estudios_indicados'
                  value={formik.values.estudios_indicados}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  minRows={2}
                  placeholder='Descripción'
                />
              </Grid>
              <Grid item xs={12}>
                <CMInputLabel mobile>Medidas generales</CMInputLabel>
                <CMTextArea
                  $spacing={2}
                  disabled={fieldsDisabled}
                  id='medidas_generales'
                  name='medidas_generales'
                  value={formik.values.medidas_generales}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  minRows={2}
                  placeholder='Descripción'
                />
              </Grid>
              {values &&
              values.doctorInformation &&
              mode !== CRUDModes.Create &&
              values.patientInformation &&
              values.doctor_id &&
              user &&
              canPrintNote(values.patientInformation, values.doctor_id, user) ? (
                <Grid item xs={12}>
                  <div style={{ display: 'none' }}>
                    <Grid
                      item
                      xs={12}
                      id='sipleCardDiv'
                      ref={printRef}
                      style={{
                        marginBottom: '1rem',
                        background: 'rgba(0,0,0,0.1)',
                        padding: '.8rem',
                        borderRadius: '10px',
                      }}
                    >
                      <NotaIndicacionesDocument
                        patient={values.patientInformation}
                        doctor={values.doctorInformation}
                        values={values as NotaIndicaciones}
                      />
                    </Grid>
                  </div>

                  {!isIOSApp() ? (
                    <PrimaryButton
                      loading={loading}
                      type='button'
                      style={{ width: '100%' }}
                      onClick={() => handlePrint()}
                    >
                      Imprimir
                    </PrimaryButton>
                  ) : (
                    <MobileDownloadMessage />
                  )}
                </Grid>
              ) : null}
              {!fieldsDisabled ? (
                <Grid item xs={12}>
                  <PrimaryButton
                    loading={loading}
                    type='button'
                    onClick={formik.handleSubmit}
                    style={{ width: '100%' }}
                  >
                    {mode === CRUDModes.Update ? 'Actualizar' : '+ Crear'}
                  </PrimaryButton>
                  {mode === CRUDModes.Update ? (
                    <PrimaryButton
                      loading={loading}
                      type='button'
                      onClick={async () => {
                        await formik.setFieldValue('isActive', false);
                        formik.handleSubmit();
                      }}
                      style={{ width: '100%', background: 'red', marginTop: '4rem' }}
                    >
                      Eliminar
                    </PrimaryButton>
                  ) : null}
                </Grid>
              ) : null}
            </Grid>
          </CenteredForm>
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

NotaIndicacionesForm.defaultProps = {
  values: undefined,
  closeFunction: () => {},
};

export default NotaIndicacionesForm;
